import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const usePayments = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [summary, setSummary] = useState({
    total: 0,
    valid: 0,
    pending: 0,
    suspense: 0,
    voided: 0,
    totalAmount: 0,
  });
  const latestPaymentRequest = useRef(0);

  /**
   * Fetch one server-paginated page of payments with optional filters
   * @param {Object} params - Query parameters for filtering
   * @param {string} params.spc - Student payment code
   * @param {string} params.status - Payment status (Valid, Pending Validation, Suspense, Invalid)
   * @param {string} params.validation_status - Validation status
   * @param {string} params.payment_method - Payment method
   * @param {string} params.date_from - Start date filter
   * @param {string} params.date_to - End date filter
   */
  const fetchPayments = useCallback(
    async (params = {}) => {
      const requestId = latestPaymentRequest.current + 1;
      latestPaymentRequest.current = requestId;
      setLoading(true);
      try {
        const queryObj = {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 10,
        };

        // Add filters if provided
        if (params.filters) {
          Object.entries(params.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              queryObj[key] = value;
            }
          });
        }

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.finance.payments}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (requestId !== latestPaymentRequest.current) return;

        if (response.ok) {
          const data = response.data || response;
          setPayments(Array.isArray(data) ? data : data.results || []);
          if (!Array.isArray(data)) {
            const total = Number(
              data.count ?? data.total ?? data.summary?.total ?? 0,
            );
            setPagination({
              current: queryObj.page,
              pageSize: queryObj.pageSize,
              total,
            });
            setSummary(
              data.summary || {
                total,
                valid: 0,
                pending: 0,
                suspense: 0,
                voided: 0,
                totalAmount: 0,
              },
            );
          }
        } else {
          Utilities.showError(response, "Failed to fetch payments");
        }
      } catch (error) {
        message.error("An error occurred while fetching payments");
        console.error(error);
      } finally {
        if (requestId === latestPaymentRequest.current) {
          setLoading(false);
        }
      }
    },
    [navigate],
  );

  /**
   * Fetch student-specific payments
   * @param {string} spc - Student payment code
   */
  const fetchStudentPayments = useCallback(
    async (spc) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.payments.replace(":spc", spc);
        const response = await Utilities.fetchWithAuth(
          "GET",
          `${url}/student/${spc}`,
          null,
          navigate,
        );

        if (response.ok) {
          return response.data;
        } else {
          Utilities.showError(response, "Failed to fetch student payments");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while fetching student payments");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Fetch payment details by ID
   * @param {number} id - Payment ID
   */
  const fetchPaymentDetails = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.paymentDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setPaymentDetails(response.data);
          return response.data;
        } else {
          Utilities.showError(response, "Failed to fetch payment details");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while fetching payment details");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Create manual payment
   * @param {Object} data - Payment data
   * @param {string} data.paymentReference - Payment reference
   * @param {string} data.spc - Student payment code
   * @param {number} data.amount - Payment amount
   * @param {string} data.paymentMethod - Payment method
   * @param {string} data.paymentChannel - Always "Manual" for manually entered payments
   * @param {string} data.paymentDate - Payment date
   * @param {string} data.status - Payment status
   */
  const createManualPayment = useCallback(
    async (data) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.payments,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Manual payment created successfully");
          fetchPayments({ page: 1 });
          return response.data;
        } else {
          Utilities.showError(response, "Failed to create manual payment");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while creating manual payment");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchPayments],
  );

  /**
   * Validate a payment (for Suspense or Pending Validation status)
   * @param {number} id - Payment ID
   * @param {Object} data - Validation data
   * @param {string} data.spc - Corrected student payment code
   * @param {string} data.notes - Validation notes
   */
  const validatePayment = useCallback(
    async (id, data) => {
      try {
        const url = domainSettings.urls.finance.paymentValidate.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Payment validated and allocated successfully");
          return response.data;
        } else {
          Utilities.showError(response, "Failed to validate payment");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while validating payment");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  /**
   * Manually allocate payment to specific fees
   * @param {number} id - Payment ID
   * @param {Object} data - Allocation data
   * @param {Array} data.allocations - Array of {feeAssignmentId, amount}
   * @param {string} data.notes - Allocation notes
   */
  const allocatePayment = useCallback(
    async (id, data) => {
      try {
        const url = domainSettings.urls.finance.paymentAllocate.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Payment allocated successfully");
          fetchPayments();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to allocate payment");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while allocating payment");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchPayments],
  );

  /**
   * Reverse all financial effects of an incorrectly entered manual payment.
   */
  const voidPayment = useCallback(
    async (id, reason) => {
      try {
        const url = domainSettings.urls.finance.paymentVoid.replace(":id", id);
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { reason },
          navigate,
        );

        if (response.ok) {
          message.success(
            response.data?.message || "Payment voided successfully",
          );
          return response.data;
        }
        Utilities.showError(response, "Failed to void payment");
        return null;
      } catch (error) {
        message.error("An error occurred while voiding the payment");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  /**
   * Apply missing automatic allocations to valid payments.
   * Existing allocations are ignored by the backend.
   * @param {Array<number>} paymentIds - Payment IDs to allocate
   */
  const allocateMissingPayments = useCallback(
    async (paymentIds, options = {}) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.paymentsAllocateMissing,
          { paymentIds },
          navigate,
        );

        if (response.ok) {
          const result = response.data;
          if (!options.silent) {
            message.success(
              `Allocation run complete: ${result.allocated || 0} applied, ${result.alreadyAllocated || 0} already allocated, ${result.skipped || 0} skipped`,
            );
          }
          if (options.refresh !== false) {
            fetchPayments();
          }
          return result;
        } else {
          Utilities.showError(response, "Failed to apply payment allocations");
          return null;
        }
      } catch (error) {
        message.error("An error occurred while applying payment allocations");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchPayments],
  );

  /**
   * Sync transactions from SchoolPay
   * @param {Object} data - Sync parameters
   * @param {string} data.date - Specific date (YYYY-MM-DD)
   * @param {string} data.from_date - Start date for range sync
   * @param {string} data.to_date - End date for range sync
   */
  const syncSchoolPayTransactions = useCallback(
    async (data = {}) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.schoolpaySyncTransactions,
          data,
          navigate,
        );

        if (response.status === 202) {
          const pendingMessage =
            response.data?.error ||
            response.data?.message ||
            "SchoolPay sync is still processing in the background";
          message.warning(pendingMessage);
          return {
            pending: true,
            message: pendingMessage,
          };
        }

        if (response.ok) {
          const result = response.data;
          message.success(
            `Sync completed: ${result.processed} processed, ${result.duplicates} duplicates, ${result.errors} errors`,
          );
          fetchPayments();
          return result;
        } else {
          Utilities.showError(
            response,
            "Failed to sync SchoolPay transactions",
          );
          return null;
        }
      } catch (error) {
        message.error("An error occurred while syncing SchoolPay transactions");
        console.error(error);
        return null;
      }
    },
    [navigate, fetchPayments],
  );

  const fetchSurePayStudentImports = useCallback(
    async (params = {}) => {
      try {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== "") {
            query.set(key, value);
          }
        });
        const url = `${domainSettings.urls.finance.surepayStudentImports}${query.toString() ? `?${query}` : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) return response.data;
        Utilities.showError(
          response,
          "Failed to load SurePay student matching records",
        );
        return null;
      } catch (error) {
        message.error(
          "An error occurred while loading SurePay student matching records",
        );
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const uploadSurePayStudentCSV = useCallback(
    async (file) => {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.surepayStudentImports,
          formData,
          navigate,
        );
        if (response.ok) {
          message.success(
            `SurePay import completed: ${response.data?.autoMatched || 0} matched, ${response.data?.unmatched || 0} unmatched, ${response.data?.manualReview || 0} for review`,
          );
          return response.data;
        }
        Utilities.showError(response, "Failed to import the SurePay CSV");
        return null;
      } catch (error) {
        message.error("An error occurred while importing the SurePay CSV");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const searchSurePayStudents = useCallback(
    async (params = {}) => {
      try {
        const query = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== "") {
            query.set(key, value);
          }
        });
        const url = `${domainSettings.urls.finance.surepayStudentSearch}${query.toString() ? `?${query}` : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) return response.data?.students || [];
        Utilities.showError(response, "Failed to search existing students");
        return [];
      } catch (error) {
        message.error("An error occurred while searching existing students");
        console.error(error);
        return [];
      }
    },
    [navigate],
  );

  const linkSurePayStudent = useCallback(
    async (importId, studentId) => {
      try {
        const url = domainSettings.urls.finance.surepayStudentLink.replace(
          ":importId",
          importId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { studentId },
          navigate,
        );
        if (response.ok) {
          message.success("SurePay student code linked successfully");
          return response.data;
        }
        Utilities.showError(
          response,
          "Failed to link the SurePay student code",
        );
        return null;
      } catch (error) {
        message.error(
          "An error occurred while linking the SurePay student code",
        );
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const addSurePayStudentCode = useCallback(
    async (surePayStudentCode, studentId, replaceExisting = false) => {
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.finance.surepayStudentCodeLink,
          { surePayStudentCode, studentId, replaceExisting },
          navigate,
        );
        if (response.ok) {
          message.success("SurePay student code assigned successfully");
          return response.data;
        }
        Utilities.showError(
          response,
          "Failed to assign the SurePay student code",
        );
        return null;
      } catch (error) {
        message.error(
          "An error occurred while assigning the SurePay student code",
        );
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const removeSurePayStudentCode = useCallback(
    async (studentId) => {
      try {
        const url =
          domainSettings.urls.finance.surepayStudentCodeUnlink.replace(
            ":studentId",
            studentId,
          );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("SurePay student code removed successfully");
          return response.data;
        }
        Utilities.showError(
          response,
          "Failed to remove the SurePay student code",
        );
        return null;
      } catch (error) {
        message.error(
          "An error occurred while removing the SurePay student code",
        );
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  return {
    payments,
    paymentDetails,
    loading,
    pagination,
    summary,
    setPagination,
    fetchPayments,
    fetchStudentPayments,
    fetchPaymentDetails,
    createManualPayment,
    validatePayment,
    allocatePayment,
    voidPayment,
    allocateMissingPayments,
    syncSchoolPayTransactions,
    fetchSurePayStudentImports,
    uploadSurePayStudentCSV,
    searchSurePayStudents,
    linkSurePayStudent,
    addSurePayStudentCode,
    removeSurePayStudentCode,
  };
};
